import { Ability, STATUS_EFFECTS } from './abilities';

export interface Character {
  id: number;
  name: string;
  class: string;
  imageUrl: string;
  baseStats: {
    health: number;
    energy: number;
    attack: number;
    defense: number;
    criticalMultiplier: number; // Multiplier for critical hits
    dodgeRate: number; // Base dodge chance
  };
  abilities: Ability[];
  ultimate: Ability;
  passive: Ability;
}

export const characters: Character[] = [
  {
    id: 1,
    name: 'Ignis',
    class: 'Fire Warrior',
    imageUrl: '/characters/fire.jpeg',
    baseStats: {
      health: 100,
      energy: 100,
      attack: 15,
      defense: 10,
      criticalMultiplier: 1.5,
      dodgeRate: 0.1
    },
    passive: {
      name: 'Burning Soul',
      type: 'buff',
      description: 'Attacks have a chance to ignite enemies',
      isPassive: true,
      energy: 0,
      criticalChance: 0.15
    },
    abilities: [
      {
        name: 'Flame Strike',
        type: 'attack',
        damage: 20,
        energy: 25,
        description: 'A powerful fire-based attack',
        statusEffect: STATUS_EFFECTS.BURN,
        criticalChance: 0.2,
        comboWith: ['Inferno Shield']
      },
      {
        name: 'Inferno Shield',
        type: 'defense',
        defense: 15,
        energy: 20,
        description: 'Creates a shield of flames that reduces incoming damage',
        statusEffect: {
          type: 'burn',
          duration: 2,
          damagePerTurn: 3,
          chance: 0.5
        }
      },
      {
        name: 'Heat Wave',
        type: 'debuff',
        damage: 10,
        energy: 30,
        description: 'Reduces enemy defense and applies burn',
        statusEffect: STATUS_EFFECTS.BURN
      }
    ],
    ultimate: {
      name: 'Inferno Blast',
      type: 'attack',
      damage: 40,
      energy: 50,
      description: 'Unleashes a devastating inferno',
      statusEffect: {
        type: 'burn',
        duration: 4,
        damagePerTurn: 8,
        chance: 0.8
      },
      criticalChance: 0.3
    }
  },
  {
    id: 2,
    name: 'Aqua',
    class: 'Water Mage',
    imageUrl: '/characters/water.jpeg',
    baseStats: {
      health: 90,
      energy: 120,
      attack: 10,
      defense: 8,
      criticalMultiplier: 1.4,
      dodgeRate: 0.15
    },
    passive: {
      name: 'Flow State',
      type: 'buff',
      description: 'Increases dodge chance when health is low',
      isPassive: true,
      energy: 0,
      dodgeChance: 0.2
    },
    abilities: [
      {
        name: 'Tidal Wave',
        type: 'attack',
        damage: 15,
        energy: 20,
        description: 'Summons a powerful wave',
        statusEffect: STATUS_EFFECTS.FREEZE,
        comboWith: ['Ice Shield']
      },
      {
        name: 'Ice Shield',
        type: 'defense',
        defense: 20,
        energy: 25,
        description: 'Creates a protective ice barrier',
        statusEffect: {
          type: 'freeze',
          duration: 1,
          chance: 0.3
        }
      },
      {
        name: 'Healing Rain',
        type: 'heal',
        healing: 15,
        energy: 30,
        description: 'Restores health over time'
      }
    ],
    ultimate: {
      name: 'Tsunami Force',
      type: 'combo',
      damage: 35,
      defense: 15,
      energy: 45,
      description: 'Massive wave that damages and freezes',
      statusEffect: STATUS_EFFECTS.FREEZE,
      criticalChance: 0.25
    }
  },
  {
    id: 3,
    name: 'Terra',
    class: 'Earth Guardian',
    imageUrl: '/characters/earth.jpeg',
    baseStats: {
      health: 120,
      energy: 90,
      attack: 12,
      defense: 15,
      criticalMultiplier: 1.3,
      dodgeRate: 0.05
    },
    passive: {
      name: 'Stone Skin',
      type: 'buff',
      description: 'Increases defense when taking damage',
      isPassive: true,
      energy: 0,
      defense: 5
    },
    abilities: [
      {
        name: 'Rock Throw',
        type: 'attack',
        damage: 18,
        energy: 20,
        description: 'Throws a large boulder',
        criticalChance: 0.15,
        comboWith: ['Stone Wall']
      },
      {
        name: 'Stone Wall',
        type: 'defense',
        defense: 25,
        energy: 25,
        description: 'Creates a defensive wall that blocks damage',
        statusEffect: {
          type: 'stun',
          duration: 1,
          chance: 0.2
        }
      },
      {
        name: 'Ground Pound',
        type: 'attack',
        damage: 15,
        energy: 30,
        description: 'Stuns enemies and deals damage',
        statusEffect: STATUS_EFFECTS.STUN
      }
    ],
    ultimate: {
      name: 'Seismic Slam',
      type: 'combo',
      damage: 45,
      energy: 55,
      description: 'Creates a massive earthquake',
      statusEffect: STATUS_EFFECTS.STUN,
      criticalChance: 0.2
    }
  },
  {
    id: 4,
    name: 'Zephyr',
    class: 'Wind Sage',
    imageUrl: '/characters/wind.png',
    baseStats: {
      health: 85,
      energy: 130,
      attack: 13,
      defense: 7,
      criticalMultiplier: 1.6,
      dodgeRate: 0.2
    },
    passive: {
      name: 'Wind Walker',
      type: 'buff',
      description: 'Increases dodge chance with each successful dodge',
      isPassive: true,
      energy: 0,
      dodgeChance: 0.1
    },
    abilities: [
      {
        name: 'Cyclone Strike',
        type: 'attack',
        damage: 16,
        energy: 15,
        description: 'Summons a powerful whirlwind',
        criticalChance: 0.25,
        comboWith: ['Wind Shield']
      },
      {
        name: 'Wind Shield',
        type: 'defense',
        defense: 12,
        energy: 20,
        description: 'Creates a barrier of swirling winds',
        dodgeChance: 0.3
      },
      {
        name: 'Gust Push',
        type: 'debuff',
        damage: 8,
        energy: 25,
        description: 'Pushes enemy back and lowers their accuracy',
        dodgeChance: 0.4
      }
    ],
    ultimate: {
      name: 'Tempest Fury',
      type: 'combo',
      damage: 38,
      energy: 48,
      description: 'Unleashes a devastating storm',
      criticalChance: 0.3,
      dodgeChance: 0.2
    }
  }
]; 